/** Axios client. Every request picks up the current bearer token; inside Teams it is fetched fresh near expiry so a long-lived tab never drifts into 401s. */
import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { toast } from 'sonner'
import { impersonationHeader, stopImpersonation } from './impersonation'
import {
  isPasswordChangeRequired,
  PASSWORD_FORGOT_PATH,
  PASSWORD_RESET_PATH,
  PASSWORD_SIGN_IN_PATH,
  PASSWORD_VERIFY_CODE_PATH,
} from './password'
import { queryClient } from './queryClient'
import { endOtpSession, getToken, hasOtpSession } from './teams'

// Marks a request already retried by `retryOnce401`, so a second 401 is not retried again.
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retriedAfter401?: boolean
  }
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
  timeout: 30_000,
  // ⛔ Without this the email-code session does not authenticate at all: its token is an HttpOnly cookie and axios omits cookies on cross-origin requests unless asked.
  // ⚠ The server must name the site's real origin in `CORS_ORIGINS`: a credentialed request against a wildcard is refused by the browser with a CORS error, not a 401.
  withCredentials: true,
})

api.interceptors.request.use(async (config) => {
  const token = await getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // View the app as somebody else. The header name and value come from POST /api/admin/impersonation, never hard-coded here.
  const impersonating = impersonationHeader()
  if (impersonating) {
    config.headers[impersonating.header] = impersonating.value
  }
  return config
})

/** True for the two endpoints that authenticate a session rather than use one (email-code sign-in). A 401 from either means a wrong or used code, never an expired session;
 *  the interceptor must not clear a just-issued token on it. Matched by suffix: `error.config.url` is bare in dev and can arrive merged and absolute in production. */
export function isUnauthenticatedAuthEndpoint(url: string | undefined): boolean {
  return Boolean(
    url &&
      (url.endsWith('/api/auth/verify-code') ||
        url.endsWith('/api/auth/request-code') ||
        // A wrong username or password is a 401 too, and must not sign out a session this browser already holds.
        url.endsWith(PASSWORD_SIGN_IN_PATH) ||
        url.endsWith(PASSWORD_VERIFY_CODE_PATH) ||
        url.endsWith(PASSWORD_FORGOT_PATH) ||
        url.endsWith(PASSWORD_RESET_PATH)),
  )
}

/** A 401 proves only that the bearer that request JUST SENT was refused, which a stale-but-recoverable credential also looks like. Nothing on the wire distinguishes the two (every 401 is a plain-string `detail`),
 *  so retry once with whatever `getToken()` returns now. `_retriedAfter401` prevents a loop and confirms the credential is dead: only then does the session clear.
 *  The OTP path has no silent refresh: `getToken()` returns null there, so the retry is skipped and it falls straight through to the clear. */
export async function retryOnce401(error: AxiosError): Promise<AxiosResponse> {
  const config = error.config
  if (config && !config._retriedAfter401) {
    const previousAuth =
      typeof config.headers?.Authorization === 'string' ? config.headers.Authorization : undefined
    const token = await getToken({ fresh: true })
    const freshAuth = token ? `Bearer ${token}` : undefined
    if (freshAuth && freshAuth !== previousAuth) {
      config._retriedAfter401 = true
      config.headers.Authorization = freshAuth
      return api.request(config)
    }
  }
  // Either this was the retry and it 401ed again, or `getToken()` had nothing new: the credential is dead. End any code session and null the cached user so the UI falls back to the sign-in gate.
  // ⛔ The `hasOtpSession()` gate is not an optimisation: `endOtpSession()` is an HTTP POST, and ungated every 401 (including all on the Teams/MSAL path) would fire a logout request.
  // Not awaited: the retry must reject with the original error now; the flag is cleared in `endOtpSession`'s `finally`.
  if (hasOtpSession()) {
    void endOtpSession()
  }
  queryClient.setQueryData(['me'], null)
  throw error
}

// 403 is "no account / inactive", a different screen, so it is left alone, EXCEPT the two unauthenticated auth endpoints above, where a 401 is a wrong code.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isAxiosError(error) && error.response) {
      // responseType:'blob' makes a 4xx JSON error body arrive as a Blob; decode it so errorMessage() shows the server's message.
      const data = error.response.data
      if (data instanceof Blob) {
        try {
          const text = await data.text()
          error.response.data = text ? JSON.parse(text) : {}
        } catch {
          // Not JSON: leave the blob as-is; errorMessage falls back to the axios message.
        }
      }
      if (error.response.status === 401 && !isUnauthenticatedAuthEndpoint(error.config?.url)) {
        return retryOnce401(error)
      }
      // An administrator-set password must be changed before anything else works. Re-reading /me flips App.tsx onto the change screen from whichever page asked. Cannot loop: /me is exempt.
      if (isPasswordChangeRequired(error.response.status, error.response.data?.detail)) {
        void queryClient.invalidateQueries({ queryKey: ['me'] })
      }
      // A 409 is a general domain conflict here, so on its own it does not prove impersonation was refused. `/api/auth/me` is the exception: a GET (so not the read-only-while-impersonating rule)
      // run behind `resolve_view_target`, so a 409 there can only be that check refusing the target. Left alone, every later request would keep 409ing and the app would read as signed out; exit and let the admin see their own view.
      if (
        error.response.status === 409 &&
        error.config?.url?.endsWith('/api/auth/me') &&
        impersonationHeader()
      ) {
        stopImpersonation()
        toast.error(`Stopped viewing as that person: ${errorMessage(error)}`)
      }
    }
    return Promise.reject(error)
  },
)

/** FastAPI puts the message in `detail`; surface that rather than "Error 409".
 *  Three shapes, and the order matters: a plain string, a list of `{msg}` objects (422), or a dict `{"code", "message"}`. The dict branch must sit AFTER `Array.isArray`, since an array is `typeof 'object'` too. */
export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.map((item: { msg?: string }) => item.msg ?? String(item)).join('; ')
    }
    // A dict `detail` carrying a human sentence. Guarded on the type, so a dict with no `message` falls through to the axios message instead of rendering "undefined".
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      return detail.message
    }
    return error.message
  }
  return error instanceof Error ? error.message : String(error)
}

/** True when the API refused this request because the caller is a platform operator rather than a tenant user.
 *  Keyed on the CODE, never the 403 (an expired token and a deactivated account answer 403 too). Strict equality, so a future refusal that grows its own `code` does not fall in.
 *  Not routed through `errorMessage()`: a boot decision must not turn on the wording of a sentence. */
export function isPlatformOperatorRefusal(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  const detail = error.response?.data?.detail
  return Boolean(detail) && typeof detail === 'object' && detail.code === 'platform_operator'
}
